import React, { useEffect, useMemo, useState } from 'react';
import styles from './HATVDashboard.module.scss';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MonthOptionsContext } from '../../Context/MonthYearOptionsContext';
import { useGetOptionsListQuery } from '../../../redux/services/optionMasterAPI';
import { useAppSelector } from '../../../redux/hooks';
import { withUtag } from '../../../utils/analytics';
import HatvInvestigation from '../HatvComponent';
import { getInitialHatvTab } from './hatvDashboardHelpers';
import HATVQMonthApprovalSection from './HATVQMonthApprovalSection/HATVQMonthApprovalSection';
import HATVAwardApprovalSection from './HATVAwardApprovalSection/HATVAwardApprovalSection';
import { HATV_NAV_LINKS } from '../../../constants/dataConstants';

interface Props {
  showHatvChatbot?: boolean;
}

const HATVDashboard: React.FC<Props> = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const searchParams = new URLSearchParams(location.search);
  const isOpenChatBot = location.state?.showHatvChatbot;
  const [isDashboardPage, setIsDashboardPage] = useState<boolean>(isOpenChatBot || false);
  const [activeLink, setActiveLink] = useState(() => getInitialHatvTab(searchParams));

  const isQMonthApproval = activeLink === 'hatv_q_month_approval';

  const {
    data: periodData,
    isLoading: optionLoading,
    isError: optionError,
    refetch: refetchOptions,
  } = useGetOptionsListQuery({
    applicationCode: 'amw',
    optionMasterName: 'myqmonthapproval-monthfilter',
  });

  const optionsData: OptionItem[] = periodData?.body?.options ?? [];

  const monthYearList = useMemo(() => {
    return optionsData.map(({ code }) => code);
  }, [optionsData]);

  const [selectedYearMonth, setSelectedYearMonth] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedYearMonth && monthYearList.length > 0) {
      setSelectedYearMonth(monthYearList[0]);
    }
  }, [monthYearList, selectedYearMonth]);
  const [selectedPYYearMonthValue, setSelectedPYYearMonthValue] = useState<string>('');

  const contextValue = {
    optionsData,
    monthYearList,
    selectedYearMonth,
    setSelectedYearMonth,
    selectedPYYear: selectedPYYearMonthValue,
    selectedPYYearMonth: setSelectedPYYearMonthValue,
    isLoading: optionLoading,
    isError: optionError,
    refetchOptions,
  };

  const handleChangeTab = (tabId: string) => {
    setActiveLink(tabId);
    localStorage.setItem('selectedHATVLink', tabId);
  };

  const handleInvestigate = () => {
    setIsDashboardPage(true);
  };

  useEffect(() => {
    const link = isQMonthApproval ? 'hatvqmonth' : 'hatvawards';
    localStorage.setItem('selectedLink', link);
  }, [isQMonthApproval]);

  useEffect(() => {
    const viewUtagObject = {
      event_name: 'hatv_view',
      am_country: localStorage?.getItem('userCountryCode') || '',
      am_affiliate: localStorage?.getItem('userAffiliateCode') || '',
      user_role: JSON.parse(localStorage.getItem('userRoleType') || '[]'),
      theme_toggle: currentTheme,
    };

    withUtag().then((utag) => utag.view(viewUtagObject));
  }, []);

  return (
    <MonthOptionsContext.Provider value={contextValue}>
      {!isDashboardPage ? (
        <div className={`hatv_dashboard ${styles.hatv_dashboard}`}>
          <div className={styles.dashboard_container}>
            <div className={`flex_wrapper ${styles.flex_wrapper}`}>
              <div className={`tab_wrapper ${styles.tab_wrapper}`}>
                {HATV_NAV_LINKS?.map((item: { mainTabID?: string }, index: number) => (
                  <div
                    key={index}
                    className={`tab_name ${styles.tab_name} ${
                      item?.mainTabID === activeLink ? `selected ${styles.selected}` : ''
                    }`}
                    onClick={() => {
                      const tabId = item?.mainTabID;
                      if (!tabId) return;
                      handleChangeTab(tabId);
                    }}
                  >
                    {item?.mainTabID ? t(`${item?.mainTabID}`) : ''}
                  </div>
                ))}
              </div>

              <div
                className={`${
                  isQMonthApproval ? styles.active_section : styles.hidden_section
                } ${styles.qmonth_section}`}
              >
                <HATVQMonthApprovalSection onInvestigate={handleInvestigate} />
              </div>
              <div
                className={
                  !isQMonthApproval ? styles.active_section : styles.hidden_section
                }
              >
                <HATVAwardApprovalSection isActive={!isQMonthApproval} />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.hatv_investigation_container}>
          <HatvInvestigation />
        </div>
      )}
    </MonthOptionsContext.Provider>
  );
};

export default HATVDashboard;
